import React, { useState, useCallback } from "react";
import { QRPay, BanksObject } from "vietnam-qr-pay";
import QRCode from "qrcode";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  Close as CloseIcon,
  Keyboard as KeyboardIcon,
  ContentPaste as PasteIcon,
  ContentCopy as CopyIcon,
  QrCode2 as QrCode2Icon,
} from "@mui/icons-material";

const typedBanksObject: Record<string, { bin?: string }> = BanksObject;

interface QRInfo {
  money: string;
  bankAccount: string;
  bankName: string;
  bankKey?: string;
  content?: string;
  orderNumber?: string;
  imageValue?: string;
}

function normalizeBankName(text: string): string {
  const nickMap: Record<string, string> = {
    "ngoai thuong": "vietcombank",
    "for foreign trade": "vietcombank",
    "cong meshoc": "vietinbank",
    "viettin": "vietinbank",
    "quan meshoc": "mbbank",
    "dau tu va phat triechs": "bidv",
    "sai gon - ha noi": "shb",
    "sai gon ha noi": "shb",
    "thịnh vưạng": "vpbank",
    "hàng hải": "msb",
    "maritime": "msb",
    "tiên phong": "tpbank",
    "tiền phong": "tpbank",
    "tp bank": "tpbank",
    "seabank": "seabank",
    "nam a": "namabank",
    "nam á": "namabank",
    "bank viet": "banviet",
    "standard chartered": "standardcharteredbank",
  };
  const lower = text.toLowerCase().trim();
  if (nickMap[lower]) return nickMap[lower];
  // Direct lookup against BanksObject keys (lowercase)
  if (typedBanksObject[lower]) return lower;
  // Strip non-alphanumeric (handles "DongA Bank" → "dongabank", "HD Bank" → "hdbank")
  const compacted = lower.replace(/[^a-z0-9]/g, "");
  if (typedBanksObject[compacted]) return compacted;
  return lower;
}

function extractData(text: string): QRInfo {
  const info: QRInfo = {
    money: "", bankAccount: "", bankName: "",
  };
  text = text.replaceAll("\n", "");

  const between = (s: string, start: string, end: string, startFrom?: number) => {
    const si = (startFrom ?? 0) >= 0 ? s.indexOf(start, startFrom) : s.indexOf(start);
    if (si < 0) return "";
    const ei = s.indexOf(end, si + start.length);
    return ei >= 0 ? s.substring(si + start.length, ei).trim() : "";
  };

  // MEXC P2P format: "Order Numberd1738467014691205120\n\nTime Created..."
  const orderRaw = between(text, "Order Number", "Time Created").trim();
  info.orderNumber = 'Chuyen tien nhanh ' + orderRaw.slice(-7);

  const acIdx = text.indexOf("Bank Account Number");
  info.bankAccount = between(text, "Bank Account Number", "Bank Name", acIdx);
  info.bankName = normalizeBankName(between(text, "Bank Name", "Branch Address", acIdx));

  const moneyRaw = between(text, "Amount", "VND");
  info.money = moneyRaw.replace(/[.,₫ ]/g, "");

  console.log("extractData:", JSON.stringify(info, null, 2));
  return info;
}

function formatVnd(raw: string): string {
  const num = parseInt(raw.replace(/[^0-9]/g, ""));
  if (isNaN(num)) return raw;
  return num.toLocaleString("vi-VN") + " VNĐ";
}

const BANK_DISPLAY: Record<string, string> = {
  abbank: "ABBank",
  acb: "ACB",
  agribank: "Agribank",
  bacabank: "BacABank",
  banviet: "Bank Viet",
  baoviet: "Baoviet",
  bidc: "Bidc",
  bidv: "BIDV",
  cake: "Cake",
  cbbank: "CBBank",
  cimb: "CMB",
  coopbank: "CoopBank",
  dbsbank: "DBS Bank",
  dongabank: "DongA Bank",
  eximbank: "Eximbank",
  gpbank: "GPBank",
  hdbank: "HD Bank",
  hongleongbank: "Hong Leong",
  hsbc: "HSBC",
  ibkhcm: "IBK HCM",
  ibkhn: "IBK HN",
  indovinabank: "Indovinabank",
  kasikorn: "KBank",
  kienlongbank: "KienLong Bank",
  kookminhcm: "Kookmin Bank HCM",
  kookminhn: "Kookmin Bank HN",
  lienvietpostbank: "LienVietPostBank",
  mbbank: "MBBank",
  msb: "MSB",
  namabank: "Nam A Bank",
  ncb: "NCB",
  nonghyup: "Nonghyup",
  ocb: "OCB",
  oceanbank: "OceanBank",
  pgbank: "PGBank",
  publicbank: "Public Bank",
  pvcombank: "PVcomBank",
  sacombank: "Sacombank",
  saigonbank: "Saigon Bank",
  scb: "SCB",
  seabank: "SeABank",
  shb: "SHB",
  shinhanbank: "Shinhan Bank",
  standardcharteredbank: "Standard Chartered",
  techcombank: "Techcombank",
  timo: "Timo",
  tpbank: "TPBank",
  ubank: "UBank",
  uob: "UOB",
  vib: "VIB",
  vietabank: "Vietabank",
  vietbank: "VietBank",
  vietcombank: "Vietcombank",
  vietinbank: "Vietinbank",
  vpbank: "VPBank",
  vrb: "VRB",
  wooribank: "Woori Bank",
};

const BANK_OPTIONS = Object.keys(typedBanksObject)
  .sort()
  .map((k) => BANK_DISPLAY[k] || k.charAt(0).toUpperCase() + k.slice(1));

// Reverse lookup: display name → BanksObject key (for manual mode)
const BANK_KEY_MAP: Record<string, string> = {};
Object.entries(BANK_DISPLAY).forEach(([key, display]) => {
  BANK_KEY_MAP[display] = key;
});

// ─── Split QR state (moved into QrModal below) ───────────────

// ─── Main Modal ──────────────────────────────────────────────
interface QrModalProps {
  open: boolean;
  onClose: () => void;
}

export default function QrModal({ open, onClose }: QrModalProps) {
  const [rawText, setRawText] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankKey, setBankKey] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [content, setContent] = useState("");
  const [fullName, setFullName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [qrCodeImage, setQrCodeImage] = useState("");
  const [qrInfo, setQrInfo] = useState<QRInfo | null>(null);
  const [activeTab, setActiveTab] = useState<"manual" | "raw">("manual");

  React.useEffect(() => {
    setBankKey(null);
  }, [activeTab]);
  const [toast, setToast] = useState<string | null>(null);
  const showToastMsg = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  // Split QR state
  const [splitVisible, setSplitVisible] = useState(false);
  const [splitQRs, setSplitQRs] = useState<{amount:number;url:string;n?:number}[]>([]);
  const [splitting, setSplitting] = useState(false);

  const splitAmount = useCallback((amount: number): number[] => {
    const result: number[] = [];
    let remaining = amount;
    const max = 499_999_999;
    while (remaining > max) {
      result.push(max);
      remaining -= max;
    }
    if (remaining > 0) result.push(remaining);
    return result;
  }, []);

  const handleSplit = async () => {
    if (!qrInfo) return;
    setSplitting(true);
    const total = parseInt(qrInfo.money || "0");
    const chunks = splitAmount(total);
    const results: {amount:number;url:string;n?:number}[] = [];

    // Resolve bank for BIN lookup — use stored key first, then display name lookup, then lowercase
    const lookupKey = qrInfo.bankKey
      || BANK_KEY_MAP[qrInfo.bankName]
      || qrInfo.bankName.toLowerCase();
    const bin = typedBanksObject[lookupKey]?.bin;
    if (!bin) {
      setErrors((p) => ({ ...p, bankName: "Không tìm thấy mã BIN" }));
      setSplitting(false);
      return;
    }

    for (const amt of chunks) {
      const pay = QRPay.initVietQR({
        bankBin: bin,
        bankNumber: qrInfo.bankAccount,
        amount: String(amt),
        purpose: (qrInfo.content || "") + " QRAPP",
      });
      const url = await QRCode.toDataURL(pay.build());
      const maxChunk = 499_999_999;
      const n = (amt === maxChunk) ? Math.floor(total / maxChunk) : undefined;
      results.push({ amount: amt, url, n });
    }
    setSplitQRs(results);
    setSplitVisible(true);
    setSplitting(false);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!/^[0-9a-zA-Z]+$/.test(accountNumber)) e.accountNumber = "Chỉ chứa số và chữ cái";
    if (!/^[a-zA-Z\s]+$/.test(bankName)) e.bankName = "Chỉ chứa chữ cái và khoảng trắng";
    if (!/^\d+$/.test(amount)) e.amount = "Chỉ chứa số";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const createQR = async (useRaw: boolean = false, providedInfo?: QRInfo) => {
    let info: QRInfo;
    if (useRaw) {
      info = providedInfo || extractData(rawText);
    } else {
      if (!validate()) return;
      const txt = [
        "Đã tạo lệnh, thanh toán cho người bán trong vòng",
        `Số lệnh${content}`,
        "Đã đặt lệnh",
        `Số tiền pháp định₫ ${amount}`,
        "Giá Nội dung chuyển khoản",
        content,
        fullName ? `Name ${fullName}` : "",
        `Bank Card/Account Number ${accountNumber}`,
        `Tên ngân hàng ${bankName}`,
      ].filter(Boolean).join("\n");
      info = extractData(txt);
      info.content = content;
      // Preserve user's display name from dropdown — extractData normalized to a key,
      // and BANK_DISPLAY might not cover all keys, causing handleSplit lookup to fail.
      info.bankName = bankName;
    }

    // Resolve bank key for BIN lookup:
    // 1. Tracked key from dropdown (manual mode)
    // 2. Display name → key reverse lookup (manual mode with freeSolo typing)
    // 3. Normalized key from extractData (raw mode)
    const lookupKey = bankKey
      || BANK_KEY_MAP[info.bankName]
      || info.bankName.toLowerCase();
    const bin = typedBanksObject[lookupKey]?.bin;
    if (!bin) {
      setErrors((p) => ({ ...p, bankName: "Không tìm thấy mã BIN" }));
      return;
    }

    info.content = info.content || info.orderNumber;
    // Resolve key → display name for raw mode
    info.bankName = BANK_DISPLAY[info.bankName.toLowerCase()] || info.bankName;
    const pay = QRPay.initVietQR({
      bankBin: bin, bankNumber: info.bankAccount,
      amount: info.money?.replace(/,/g, ""),
      purpose: (info.content || "") + " QRAPP",
    });

    const img = await QRCode.toDataURL(pay.build());
    if (!img) return;
    setQrCodeImage(img);
    setQrInfo({ ...info, imageValue: img, bankKey: bankKey || undefined });
  };

  const handlePaste = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      setRawText(txt);
      const info = extractData(txt);
      await createQR(true, info);
      showToastMsg("Đã dán từ clipboard!");
    } catch { showToastMsg("Không đọc được clipboard"); }
  };

  const handleCopy = () => {
    if (!qrInfo) return;
    const txt = [
      `Số tiền: ${formatVnd(qrInfo.money)}`,
      `STK: ${qrInfo.bankAccount}`,
      `Ngân hàng: ${qrInfo.bankName}`,
      `Nội dung: ${qrInfo.content || ""}`,
    ].join("\n");
    navigator.clipboard.writeText(txt).then(
      () => showToastMsg("Đã sao chép thông tin!"),
      () => showToastMsg("Không sao chép được")
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={false}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "95vw",
          maxHeight: "92vh",
          bgcolor: "#0f172a",
          color: "#e2e8f0",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #1e293b",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#38bdf8" }}>
            QR Code Generator
          </Typography>
          <Chip label="VietQR" size="small" sx={{
            bgcolor: "rgba(56,189,248,0.1)", color: "#38bdf8", fontSize: "0.7rem", height: 20,
          }} />
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#94a3b8", "&:hover": { color: "#e2e8f0" } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Toast */}
      {toast && (
        <Box sx={{
          position: "absolute", top: 60, left: "50%", transform: "translateX(-50%)",
          bgcolor: "#22c55e", color: "#fff", px: 2, py: 0.75, borderRadius: 2,
          fontSize: "0.8rem", zIndex: 100, boxShadow: 6,
        }}>
          {toast}
        </Box>
      )}

      <DialogContent sx={{
        p: 0, display: "flex", overflow: "auto",
      }}>
        {/* Left Panel — Input */}
        <Box sx={{
          flex: "0 0 55%", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5,
          borderRight: "1px solid #1e293b", overflowY: "auto",
          maxHeight: "75vh",
        }}>
          {/* Tabs */}
          <Box sx={{ display: "flex", gap: 0.5, bgcolor: "#1e293b", borderRadius: 2, p: 3 }}>
            {(["manual", "raw"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  flex: 1, padding: "8px 16px", border: "none", borderRadius: 14,
                  cursor: "pointer", fontWeight: activeTab === tab ? 700 : 400,
                  fontSize: "0.85rem",
                  backgroundColor: activeTab === tab ? "#3b82f6" : "transparent",
                  color: activeTab === tab ? "#fff" : "#9ca3af",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  transition: "all 0.2s",
                }}
              >
                {tab === "manual" ? <KeyboardIcon sx={{ fontSize: 16 }} /> : <PasteIcon sx={{ fontSize: 16 }} />}
                {tab === "manual" ? "Nhập tay" : "Dán văn bản"}
              </button>
            ))}
          </Box>

          {/* Manual mode */}
          {activeTab === "manual" && (
            <>
              <TextField
                label="Họ tên (tuỳ chọn)"
                placeholder="Nguyễn Văn A"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                size="small"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 } }}
              />
              <TextField
                label="Số tài khoản"
                placeholder="0123456789"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
                size="small"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 } }}
              />
              <Autocomplete
                freeSolo
                options={BANK_OPTIONS}
                value={bankName}
                onChange={(_e, newValue) => {
                  setBankName(newValue || "");
                  setBankKey(newValue ? (BANK_KEY_MAP[newValue] || null) : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tên ngân hàng"
                    placeholder="Tìm và chọn ngân hàng..."
                    error={!!errors.bankName}
                    helperText={errors.bankName || "Nhập để tìm kiếm ngân hàng"}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ px: 2, py: 0.75, fontSize: "0.875rem" }}>
                    {option}
                  </Box>
                )}
                autoHighlight
                disableCloseOnSelect
              />
              <TextField
                label="Số tiền"
                placeholder="100000"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/,/g, ""))}
                error={!!errors.amount}
                helperText={errors.amount}
                size="small"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 } }}
              />
              <TextField
                label="Nội dung chuyển khoản (tuỳ chọn)"
                placeholder="Mã đơn hàng..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                size="small"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 } }}
              />

              <Button
                variant="contained"
                onClick={() => createQR(false)}
                sx={{
                  mt: 0.5, py: 1.2, fontWeight: 700, fontSize: "1rem",
                  bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" },
                  borderRadius: 2,
                }}
              >
                TẠO MÃ QR
              </Button>

            </>
          )}

          {/* Raw text mode */}
          {activeTab === "raw" && (
            <>
              <TextField
                multiline
                label="Dán văn bản P2P"
                placeholder="Dán nội dung lệnh P2P từ clipboard..."
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                fullWidth
                InputProps={{
                  sx: { minHeight: 80, maxHeight: 150, overflow: "auto" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "#1e293b", borderRadius: 1.5 },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const info = extractData(rawText);
                    createQR(true, info);
                  }}
                  disabled={!rawText.trim()}
                  sx={{
                    flex: 1, borderRadius: 2,
                    bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" },
                  }}
                >
                  Tạo QR từ văn bản
                </Button>
                <Button
                  variant="outlined"
                  onClick={handlePaste}
                  startIcon={<PasteIcon />}
                  sx={{ borderRadius: 2, color: "#93c5fd", borderColor: "#3b82f6" }}
                >
                  Dán clipboard
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* Right Panel — QR Display */}
        <Box sx={{
          flex: "0 0 45%", p: 2.5, bgcolor: "#0b1120",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 1.5,
          maxHeight: "75vh", overflowY: "auto",
        }}>
          {qrInfo ? (
            <>
              {/* Info cards */}
              <Box sx={{
                width: "100%", bgcolor: "#1e293b", borderRadius: 2, p: 2,
                border: "1px solid #334155",
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>Số tiền:</Typography>
                  <Typography variant="body2" sx={{ color: "#f87171", fontWeight: 700, textAlign: "right" as const }}>
                    {formatVnd(qrInfo.money)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>STK:</Typography>
                  <Typography variant="body2" sx={{ color: "#e2e8f0", textAlign: "right" as const, fontFamily: "monospace" }}>
                    {qrInfo.bankAccount}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>Ngân hàng:</Typography>
                  <Typography variant="body2" sx={{ color: "#60a5fa", fontWeight: 600, textAlign: "right" as const }}>
                    {qrInfo.bankName}
                  </Typography>
                </Box>
                {qrInfo.content && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>Nội dung:</Typography>
                    <Typography variant="body2" sx={{ color: "#e2e8f0", textAlign: "right" as const }}>
                      {qrInfo.content}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* QR Image */}
              <Box sx={{
                position: "relative", display: "inline-block", p: 2,
                bgcolor: "#f8fafc", borderRadius: 2,
              }}>
                <img src={qrCodeImage} alt="QR Code" style={{ maxWidth: 180, display: "block" }} />
                <Box sx={{
                  position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                  border: "2px dashed #3b82f6", borderRadius: 2, pointerEvents: "none",
                }} />
              </Box>

              {/* Actions */}
              <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                <Button
                  variant="outlined"
                  onClick={handleCopy}
                  startIcon={<CopyIcon />}
                  fullWidth
                  sx={{
                    color: "#93c5fd", borderColor: "#334155", borderRadius: 2,
                    "&:hover": { borderColor: "#60a5fa", backgroundColor: "rgba(59,130,246,0.08)" },
                  }}
                >
                  Sao chép thông tin
                </Button>
              </Box>

              {/* Split QR */}
              {qrInfo && (
                <Box sx={{ width: "100%" }}>
                  <Button
                    variant="outlined"
                    onClick={handleSplit}
                    disabled={splitting}
                    fullWidth
                    sx={{
                      color: "#fbbf24", borderColor: "#f59e0b", borderRadius: 2,
                      mb: 1, "&:hover": { borderColor: "#fbbf24", backgroundColor: "rgba(251,191,36,0.08)" },
                    }}
                  >
                    {splitting ? "Đang chia..." : "Chia QR"}
                  </Button>
                  {splitVisible && splitQRs.length > 0 && (
                    <Box sx={{
                      bgcolor: "#1e293b", borderRadius: 2, p: 2,
                      border: "1px solid #334155", mt: 1,
                    }}>
                      <Typography variant="body2" sx={{ color: "#fbbf24", fontWeight: 700, mb: 1.5 }}>
                        🔀 Đã chia thành {splitQRs.length} mã QR
                      </Typography>
                      {splitQRs.map((sq, idx) => {
                        const maxChunk = 499_999_999;
                        const isMax = sq.amount === maxChunk;
                        return (
                          <Box key={idx} sx={{ mb: idx < splitQRs.length - 1 ? 2 : 0 }}>
                            <Box sx={{
                              p: 1.5, borderRadius: 2,
                              border: isMax ? "2px solid #f59e0b" : "2px solid #22c55e",
                              backgroundColor: isMax ? "rgba(245,158,36,0.05)" : "rgba(34,197,94,0.05)",
                              display: "flex", gap: 2, alignItems: "center",
                            }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                  <Chip
                                    label={isMax ? `Quét ${sq.n || 1} lần` : "Quét 1 lần"}
                                    size="small"
                                    sx={{
                                      bgcolor: isMax ? "rgba(245,158,36,0.2)" : "rgba(34,197,94,0.2)",
                                      color: isMax ? "#fbbf24" : "#4ade80",
                                      fontSize: "0.7rem", height: 18,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: isMax ? "#fbbf24" : "#4ade80", fontWeight: 700 }}>
                                    {sq.amount.toLocaleString("vi-VN")} VNĐ
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                                  STK: {sq.n ? `${sq.n} × ${maxChunk.toLocaleString("vi-VN")}` : sq.amount.toLocaleString("vi-VN")}
                                </Typography>
                              </Box>
                              <img src={sq.url} alt={`QR ${sq.amount}`} style={{ width: 100, height: 100, borderRadius: 8, backgroundColor: "#fff", padding: 4 }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            /* Placeholder */
            <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ fontSize: 56, color: "#3b82f6" }}>
                <QrCode2Icon sx={{ fontSize: 56 }} />
              </Box>
              <Typography variant="h6" sx={{ color: "#e2e8f0", fontWeight: 700 }}>
                Tạo mã QR của bạn
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 260 }}>
                Điền thông tin bên trái để tạo mã QR thanh toán VietQR
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 0.5 }}>
                {[
                  "1. Nhập thông tin thanh toán",
                  "2. Nhấn \"TẠO MÃ QR\"",
                  "3. QR xuất hiện tại đây",
                ].map((step, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: "50%", bgcolor: "#3b82f6",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
                    }}>
                      {i + 1}
                    </Box>
                    <Typography variant="body2" sx={{ color: "#cbd5e1" }}>{step}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
